import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FaClock, FaBoxOpen, FaUser, FaComments } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

export default function ProductQuoteRequests() {
  const { API, user } = useContext(AppContext);
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    fetchQuotes();
  }, [user]);

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes/vendor/requests?user_id=${user.id}`);
      setQuotes(res.data);
    } catch (e) {
      console.error('Error loading vendor quote requests', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US');
  };

  const getStatusLabel = (status) => {
    if (i18n.language === 'fr') {
      if (status === 'quoted') return 'Proposition envoyée';
      if (status === 'contacted') return 'En discussion';
      if (status === 'closed') return 'Clôturé';
      if (status === 'accepted') return 'Validé';
      if (status === 'shipped') return 'Expédié';
      return 'En attente';
    }
    if (status === 'quoted') return 'Quote sent';
    if (status === 'contacted') return 'In discussion';
    if (status === 'closed') return 'Closed';
    if (status === 'accepted') return 'Accepted';
    if (status === 'shipped') return 'Shipped';
    return 'Pending';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
              {i18n.language === 'fr' ? 'Demandes de devis produits' : 'Product quote requests'}
            </h1>
            <p className="text-gray-600">
              {i18n.language === 'fr'
                ? 'Répondez aux demandes de devis envoyées depuis les fiches produits.'
                : 'Reply to quote requests sent from product pages.'}
            </p>
          </div>
        </div>

        {quotes.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-600">
                {i18n.language === 'fr'
                  ? "Vous n'avez pas encore de demandes de devis produits."
                  : 'You do not have any product quote requests yet.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {quotes.map((q) => (
              <Card key={q.id} className="hover:shadow-md transition">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-semibold text-[#0A2540] mb-1">
                        {q.product_name}
                      </h2>
                      <p className="text-sm text-gray-600 flex items-center gap-2 mb-1">
                        <FaUser />
                        <span>{q.user_name}</span>
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-2 mb-2">
                        <FaClock />
                        <span>{formatDate(q.created_at)}</span>
                      </p>
                      {q.message && (
                        <p className="text-sm text-gray-500 line-clamp-2">{q.message}</p>
                      )}
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                        {getStatusLabel(q.status)}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => navigate(`/supplier/quotes/${q.id}`)}
                      >
                        <FaComments className="mr-2" />
                        {i18n.language === 'fr' ? 'Ouvrir la discussion' : 'Open discussion'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
